import React, { useState, useRef } from "react";
import Variant from "./Variant";
import "../../css/AddTripForm/Trip.css"; 
import { useTripContext } from "../Trips/TripContext";
import FormField from "./FormField";
import { validateVariantInputs } from "./validateInputs";
import { formatDate } from "../Common/dateManager";
import { useToast } from "../../Contexts/ToastContext";
import VariantChoicePopup from "./VariantChoicePopup";

const Trip = ({ onCancelEdit }) => {
  const {
    tripData,
    deleteVariant,
    addElementToVariant,
    updateTripBaseInfo,
    removeElementFromVariant,
    loading,
    error,
    selectedVariantGuid,
    setSelectedVariantGuid,
    showVariantInForm,
    setShowVariantInForm
  } = useTripContext();

  const { guid, name, description, photoSource, isInternalTrip, photoLink } = tripData;
  const {showToast, clearToast} = useToast();
  const [tripBasicInfo, setTripBasicInfo] = useState({
    guid,
    name,
    description,
    photoSource,
    isInternalTrip,
    photoLink
  });

  const [showVariantChoice, setShowVariantChoice] = useState(false);
const [copiedVariantData, setCopiedVariantData] = useState(null);

  const formRef = useRef(null);

  const handleTripChange = (field, value) => {
    if (value === null || value === undefined || value === "" || value.trim?.() === "") {
      value = null;
    } else if (typeof value === "boolean") {
      // nic
    } else if (!isNaN(value)) {
      value = Number(value);
    }

    setTripBasicInfo((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const onSaveVariantClick = async () => {
    setSelectedVariantGuid(null);
    setShowVariantInForm(false);
    setCopiedVariantData(null);
  };

  const onUpdateVariant = async () => {
    setSelectedVariantGuid(null);
    setShowVariantInForm(false);
  };

  const handleClickVariant = (variant) => {
    setCopiedVariantData(null); 
    setSelectedVariantGuid(variant.guid);
    setShowVariantInForm(true);
  };

  const onClickRemoveElement = (variantId, elementId) => {
    removeElementFromVariant(variantId, elementId);
  };

  const onDeleteVariant = async () => {
    setSelectedVariantGuid(null);
    setShowVariantInForm(false);
    setCopiedVariantData(null);
  };

  const handleNewVariant = () => {
  setShowVariantChoice(false);
  setSelectedVariantGuid(null);
  setCopiedVariantData(null);
  setShowVariantInForm(true);
};

const handleCopyVariant = (sourceGuid) => {
  const source = tripData.variants.find(v => v.guid === sourceGuid);
  if (!source) return;

  const { guid, elements, ...rest } = source;

  const copied = {
    ...rest,
    guid: null,
    name: source.name + " (kopia)",
    variantPhotoLink: null,
    variantPhotoSource: null
  };

  setCopiedVariantData(copied);
  setSelectedVariantGuid(null);
  setShowVariantChoice(false);
  setShowVariantInForm(true);
};


  const handleSaveTrip = async () => {
    try {
    if (validateVariantInputs(formRef)) {
      showToast("Zapisywanie wycieczki...", 'processing');
      await updateTripBaseInfo(tripBasicInfo);
      await onCancelEdit(true);
    }
  } finally {
    clearToast();
  }
  };

  return (
    <div className="tripContainer" ref={formRef}>
      <FormField
        label="Nazwa wycieczki"
        name="name"
        value={tripBasicInfo.name}
        onChange={handleTripChange}
        placeholder="Podaj nazwę wycieczki"
        required
      />
      <FormField
        label="Zdjęcie"
        name="photo"
        type="file"
        onChange={handleTripChange}
        value={tripBasicInfo.photoLink}
        photoLinkName="photoLink"
        photoSourceName="photoSource"
      />
      <FormField
        label="Źródło zdjęcia"
        name="photoSource"
        value={tripBasicInfo.photoSource || ""}
        onChange={handleTripChange}
        placeholder={
          (!tripBasicInfo.photo && !tripBasicInfo.photoLink)
            ? "--- Nie wybrano zdjęcia ---"
            : "Podaj źródło zdjęcia"
        }
        disabled={!tripBasicInfo.photo && !tripBasicInfo.photoLink}
      />
      <FormField
        label="Opis wycieczki"
        name="description"
        type="textarea"
        value={tripBasicInfo.description}
        onChange={handleTripChange}
        placeholder="Podaj krótki opis wycieczki"
        info="Krótki opis wycieczki, który będzie wyświetlany w publicznym widoku wszystkich wycieczek."
      />
      <FormField
        label="Wycieczka zamknięta"
        name="isInternalTrip"
        type="toggle"
        value={tripBasicInfo.isInternalTrip}
        onChange={handleTripChange}
      />

      {!showVariantInForm && (
        <div className="variantsSection">
          <div className="variantsHeader">
            <div className="variantsTitle">Warianty wycieczki:</div>
          <div
            className="addVariantButton"
            onClick={() => setShowVariantChoice(true)}
            role="button"
            title="Dodaj wariant"
          >
            <i className="fas fa-plus addVariantIcon"></i>
          </div>
          </div>
          {tripData.variants.length === 0 ? (
            <div className="noVariantsMessage">
              <em>
                Brak dodanych wariantów. Kliknij ikonę + po prawej stronie aby
                dodać nowy wariant.
              </em>
            </div>
          ) : (
            <div className="tripform-variants-list">
              {tripData.variants.map((variant) => (
                <div
                  key={variant.guid}
                  className="tripform-variant-item"
                  onClick={() => handleClickVariant(variant)}
                >
                  <div className="tripform-icon-container">
                    <i className="fas fa-route" title="Wariant wycieczki"></i>
                  </div>
                  <div className="tripform-variant-content">
                    <div className="tripform-variant-name"><strong>{variant.name}</strong></div>
                    <div className="tripform-variant-dates">
                      <em>{formatDate(variant.startDate)} – {formatDate(variant.endDate)}</em>
                    </div>
                    {variant.coordinators.length > 0 && (
                      <div className="tripform-variant-status">
                        {variant.coordinators.map((c) => c.name).join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showVariantInForm && (
        <Variant
          onSave={onSaveVariantClick}
          onUpdate={onUpdateVariant}
          onDelete={onDeleteVariant}
          addElementToVariant={addElementToVariant}
          removeElementFromVariant={onClickRemoveElement}
          onUpdateElement={() => setShowVariantInForm(true)}
          initialData={copiedVariantData}
        />
      )}

{!showVariantInForm && (
      <div className="actions">
        <button
          className="btnDanger"
          onClick={() => {
            if (
              window.confirm(
                "Czy na pewno chcesz przerwać edycję wycieczki? Zmiany wprowadzone na formularzu wycieczki nie zostaną zapisane."
              )
            ) {
              onCancelEdit();
            }
          }}
          disabled={loading}
        >
          Porzuć zmiany
        </button>

        <button className="btnPrimary" onClick={handleSaveTrip} disabled={loading}>
          {loading ? (
            <>
              <i className="fa fa-spinner fa-spin" style={{ marginRight: "8px" }} />
              Zapisywanie...
            </>
          ) : (
            "Zapisz wycieczkę"
          )}
        </button>
      </div>
    )}

    {showVariantChoice && (
  <VariantChoicePopup
    variants={tripData.variants}
    onNew={handleNewVariant}
    onCopy={handleCopyVariant}
    onClose={() => setShowVariantChoice(false)}
  />
)}
    </div>
  );
};

export default Trip;
